import type { NavigationItem } from 'v2/layouts/NavigationLayout/types';
import AircraftTableSection from 'v2/layouts/OrganizationWorkspaceLayout/components/AircraftTableSection';
import ControllerTableSection from 'v2/layouts/OrganizationWorkspaceLayout/components/ControllerTableSection';
import InvitationsTableSection from 'v2/layouts/OrganizationWorkspaceLayout/components/InvitationsTableSection';
import OrganizationDetailsSection from 'v2/layouts/OrganizationWorkspaceLayout/components/OrganizationDetailsSection';
import UsersTableSection from 'v2/layouts/OrganizationWorkspaceLayout/components/UsersTableSection';

import CustomerIcon from 'components/icons/CustomerIcon';
import EquipmentIcon from 'components/icons/EquipmentIcon';
import OrganizationIcon from 'components/icons/OrganizationIcon';
import SubscriptionIcon from 'components/icons/SubscriptionIcon';

import {
    EDIT_AIRCRAFT_PAGE,
    EDIT_CONTROLLER_PAGE,
    MANAGE_ORGANIZATION_PAGE,
    ORGANIZATION_DETAILS,
    ORGANIZATION_EDIT_MEMBER,
    ORGANIZATION_EQUIPMENT,
    ORGANIZATION_EQUIPMENT_AIRCRAFT,
    ORGANIZATION_EQUIPMENT_CONTROLLER,
    ORGANIZATION_INVITATIONS,
    ORGANIZATION_NEW_AIRCRAFT,
    ORGANIZATION_NEW_CONTROLLER,
    ORGANIZATION_NEW_INVITATION,
    ORGANIZATION_NEW_MEMBER,
    ORGANIZATION_SUBSCRIPTION,
    ORGANIZATION_USERS
} from 'hooks/usePageRoutes/constants';

import Subscriptions from 'pages/AccountDrawerPage/AccountDrawerTabs/Subscriptions';

import english from './translations/english';
import type {
    OrganizationWorkspaceLocation,
    OrganizationWorkspaceSection,
    OrganizationWorkspaceTab
} from './types';

export {
    EDIT_PANEL_ROUTE_PREFIXES,
    MEMBER_ROUTE_PREFIXES,
    ORGANIZATION_EDIT_ROUTE,
    ORGANIZATION_NEW_ROUTE,
    getIsEditPanelOpen,
    getOpenedMemberId
} from './routing';

export const TEST_ID = 'organizationWorkspaceLayout';

export const EQUIPMENT_SECTION = 'equipment';

export const ORGANIZATION_SECTION = 'organization';

export const SUBSCRIPTION_SECTION = 'subscription';

export const USERS_SECTION = 'users';

export const AIRCRAFT_TAB = 'aircraft';

export const CONTROLLER_TAB = 'controller';

export const DETAILS_TAB = 'details';

export const INVITATIONS_TAB = 'invitations';

export const SUBSCRIPTION_TAB = 'subscription';

export const USERS_TAB = 'users';

export const FIRST_TAB_INDEX = 0;

export const INITIAL_SECTION_CONTENT_KEY = 0;

export const SECTION_CONTENT_KEY_INCREMENT = 1;

export const NOT_FOUND_TAB_INDEX = -1;

export const SECTION_TABS: Record<OrganizationWorkspaceSection, OrganizationWorkspaceTab[]> = {
    [EQUIPMENT_SECTION]: [
        {
            addRoute: ORGANIZATION_NEW_AIRCRAFT,
            content: AircraftTableSection,
            id: AIRCRAFT_TAB,
            route: ORGANIZATION_EQUIPMENT_AIRCRAFT
        },
        {
            addRoute: ORGANIZATION_NEW_CONTROLLER,
            content: ControllerTableSection,
            id: CONTROLLER_TAB,
            route: ORGANIZATION_EQUIPMENT_CONTROLLER
        }
    ],
    [ORGANIZATION_SECTION]: [
        { content: OrganizationDetailsSection, id: DETAILS_TAB, route: ORGANIZATION_DETAILS }
    ],
    [SUBSCRIPTION_SECTION]: [
        { content: Subscriptions, id: SUBSCRIPTION_TAB, route: ORGANIZATION_SUBSCRIPTION }
    ],
    [USERS_SECTION]: [
        {
            content: UsersTableSection,
            id: USERS_TAB,
            route: ORGANIZATION_USERS
        },
        {
            addRoute: ORGANIZATION_NEW_INVITATION,
            content: InvitationsTableSection,
            id: INVITATIONS_TAB,
            route: ORGANIZATION_INVITATIONS
        }
    ]
};

export const DEFAULT_WORKSPACE_LOCATION: OrganizationWorkspaceLocation = {
    section: ORGANIZATION_SECTION,
    tabId: DETAILS_TAB
};

export const WORKSPACE_LOCATIONS: {
    location: OrganizationWorkspaceLocation;
    pathPrefix: string;
}[] = [
    {
        location: { section: EQUIPMENT_SECTION, tabId: AIRCRAFT_TAB },
        pathPrefix: EDIT_AIRCRAFT_PAGE
    },
    {
        location: { section: EQUIPMENT_SECTION, tabId: CONTROLLER_TAB },
        pathPrefix: EDIT_CONTROLLER_PAGE
    },
    {
        location: { section: EQUIPMENT_SECTION, tabId: AIRCRAFT_TAB },
        pathPrefix: ORGANIZATION_NEW_AIRCRAFT
    },
    {
        location: { section: EQUIPMENT_SECTION, tabId: CONTROLLER_TAB },
        pathPrefix: ORGANIZATION_NEW_CONTROLLER
    },
    {
        location: { section: EQUIPMENT_SECTION, tabId: CONTROLLER_TAB },
        pathPrefix: ORGANIZATION_EQUIPMENT_CONTROLLER
    },
    {
        location: { section: EQUIPMENT_SECTION, tabId: AIRCRAFT_TAB },
        pathPrefix: ORGANIZATION_EQUIPMENT
    },
    {
        location: { section: USERS_SECTION, tabId: INVITATIONS_TAB },
        pathPrefix: ORGANIZATION_NEW_INVITATION
    },
    {
        location: { section: USERS_SECTION, tabId: INVITATIONS_TAB },
        pathPrefix: ORGANIZATION_INVITATIONS
    },
    {
        location: { section: USERS_SECTION, tabId: USERS_TAB },
        pathPrefix: ORGANIZATION_USERS
    },
    {
        location: { section: USERS_SECTION, tabId: USERS_TAB },
        pathPrefix: ORGANIZATION_EDIT_MEMBER
    },
    {
        location: { section: USERS_SECTION, tabId: USERS_TAB },
        pathPrefix: ORGANIZATION_NEW_MEMBER
    },
    {
        location: { section: SUBSCRIPTION_SECTION, tabId: SUBSCRIPTION_TAB },
        pathPrefix: ORGANIZATION_SUBSCRIPTION
    }
];

export const ORGANIZATION_NAVIGATION_ITEMS: NavigationItem[] = [
    {
        activePathPrefixes: [MANAGE_ORGANIZATION_PAGE],
        href: ORGANIZATION_DETAILS,
        icon: OrganizationIcon,
        id: ORGANIZATION_SECTION,
        label: english.organization
    },
    {
        activePathPrefixes: [
            ORGANIZATION_EQUIPMENT,
            EDIT_AIRCRAFT_PAGE,
            EDIT_CONTROLLER_PAGE,
            ORGANIZATION_NEW_AIRCRAFT,
            ORGANIZATION_NEW_CONTROLLER
        ],
        href: ORGANIZATION_EQUIPMENT_AIRCRAFT,
        icon: EquipmentIcon,
        id: EQUIPMENT_SECTION,
        label: english.equipment
    },
    {
        activePathPrefixes: [
            ORGANIZATION_USERS,
            ORGANIZATION_INVITATIONS,
            ORGANIZATION_NEW_INVITATION,
            ORGANIZATION_EDIT_MEMBER,
            ORGANIZATION_NEW_MEMBER
        ],
        href: ORGANIZATION_USERS,
        icon: CustomerIcon,
        id: USERS_SECTION,
        label: english.users
    },
    {
        href: ORGANIZATION_SUBSCRIPTION,
        icon: SubscriptionIcon,
        id: SUBSCRIPTION_SECTION,
        label: english.subscription
    }
];

export const getWorkspaceLocation = (pathname: string): OrganizationWorkspaceLocation =>
    WORKSPACE_LOCATIONS.find(workspaceLocation => pathname.startsWith(workspaceLocation.pathPrefix))
        ?.location ?? DEFAULT_WORKSPACE_LOCATION;
